<div class="container mx-auto px-4 py-8">
  <div class="mb-12 text-center">
    <h1 class="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
      AI News and Blog Updates
    </h1>
    <p class="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
      Explore the latest AI and machine learning news, insights, and technology updates.
    </p>
  </div>

  <!-- Search bar and filters -->
  <div class="mb-8 flex flex-col items-center justify-center gap-4 md:flex-row">
    <div class="relative w-full max-w-md">
      <label hlmLabel for="newsSearch" class="sr-only">Search News</label>
      <input
        hlmInput
        id="newsSearch"
        class="pl-10 w-full pr-10"
        placeholder="Search articles..."
        [formControl]="searchInputControl"
      />
      <ng-icon name="lucideSearch" class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      @if (searchInputControl.value) {
        <button
          hlmBtn
          variant="ghost"
          size="icon"
          class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full text-muted-foreground hover:text-foreground"
          (click)="clearSearch()"
          aria-label="Clear search"
        >
          <ng-icon hlm name="lucideX" size="sm" />
        </button>
      }
    </div>
  </div>

  <!-- Search results message when searching -->
  @if (search() && articles().length > 0) {
    <div class="mb-6 text-center">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Showing results for "<span class="font-medium">{{ search() }}</span
        >"
        <button hlmBtn variant="ghost" size="sm" class="ml-2 h-8 px-2 py-0" (click)="clearSearch()">Clear</button>
      </p>
    </div>
  }

  <!-- Filters and Sort Controls - mobile responsive -->
  <div class="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
    <!-- Article Type Filter Buttons on the left -->
    <div class="flex flex-wrap gap-2">
      <button
        hlmBtn
        [variant]="typeFilter() === 'all' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('all')"
      >
        All
      </button>
      <button
        hlmBtn
        [variant]="typeFilter() === 'news' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('news')"
      >
        <ng-icon name="lucideNewspaper" class="mr-1.5" />
        News
      </button>
      <button
        hlmBtn
        [variant]="typeFilter() === 'blog' ? 'default' : 'outline'"
        size="sm"
        (click)="onTypeFilterChange('blog')"
      >
        <ng-icon name="lucideBookOpen" class="mr-1.5" />
        Blog
      </button>
    </div>

    <!-- Time Filter Select -->
    <div class="w-full sm:w-auto sm:min-w-[200px]">
      <hlm-select class="w-full" [formControl]="sortControl">
        <hlm-select-trigger class="w-full">
          <hlm-select-value />
        </hlm-select-trigger>
        <hlm-select-content>
          @for (option of sortOptions; track option.value) {
            <hlm-option [value]="option.value">{{ option.label }}</hlm-option>
          }
        </hlm-select-content>
      </hlm-select>
    </div>
  </div>

  @if (loading()) {
    <div class="flex justify-center py-10">
      <app-loading-spinner />
    </div>
  } @else if (error()) {
    <app-error-message [message]="error()!" (retry)="loadArticles(page(), pageSize(), search())" />
  } @else if (articles().length === 0) {
    <div class="py-10 text-center">
      <ng-icon hlm name="lucideFileText" size="xl" class="mb-4 inline-block text-muted-foreground" />
      <p class="text-xl text-muted-foreground">No news articles found matching your criteria.</p>
      @if (search()) {
        <p class="mt-2 text-muted-foreground">Try adjusting your search or clearing the filter.</p>
        <button hlmBtn variant="outline" class="mt-4" (click)="clearSearch()">Clear Search</button>
      }
    </div>
  } @else {
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      @for (article of articles(); track article.id) {
        <app-news-card [article]="article" [searchTerm]="search()" />
      }
    </div>

    <!-- Pagination -->
    <div class="mt-8 flex justify-center">
      <hlm-numbered-pagination
        [totalItems]="totalItems()"
        [currentPage]="page()"
        [itemsPerPage]="pageSize()"
        [maxSize]="responsiveMaxSize()"
        [pageSizes]="[12, 24, 60, 120]"
        (currentPageChange)="onPageChange($event)"
        (itemsPerPageChange)="onPageSizeChange($event)"
      />
    </div>
  }
</div>
